import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";
import { LOGO_PNG } from "../data/site";
import { Button } from "./ui/button";
import { NAVIGATION, NAV_CTA_LABEL, NAV_CTA_ROLES } from "../data/home-data";

const NAV = NAVIGATION;

export default function Navbar({ bannerVisible = true, bannerHeight = 44 }) {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [drop, setDrop] = useState(null);
    const [cta, setCta] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const goWaitlist = (role) => {
        setCta(false);
        navigate(`/waitlist?role=${role}`);
    };

    return (
        <header
            className="fixed left-0 right-0 z-50 px-3 sm:px-6 transition-all duration-300"
            style={{ top: bannerVisible ? `${bannerHeight + 8}px` : "12px" }}
            data-testid="site-navbar"
        >
            {/* Wrapper for mouse-leave detection on both navbar + mega menu */}
            <div
                className="max-w-7xl mx-auto relative"
                onMouseLeave={() => setDrop(null)}
            >
                {/* Navbar Pill — always stays pill-shaped */}
                <motion.nav
                    initial={{ y: -30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className={`relative z-20 flex items-center justify-between px-4 sm:px-6 py-3 transition-all duration-300 ${
                        drop
                            ? "bg-white rounded-t-[28px] rounded-b-none shadow-none border border-gray-100 border-b-0"
                            : scrolled
                              ? "bg-white/90 backdrop-blur-xl shadow-lg border border-gray-200 rounded-full"
                              : "bg-white/60 backdrop-blur-md border border-white/30 rounded-full"
                    }`}
                >
                    {/* Logo */}
                    <Link
                        to="/"
                        className="flex items-center gap-2 shrink-0"
                        data-testid="nav-logo-link"
                    >
                        <img
                            src={LOGO_PNG}
                            alt="AI Prof"
                            className="h-10 w-auto object-contain"
                        />
                    </Link>

                    {/* Desktop Nav */}
                    <div className="hidden lg:flex items-center gap-3 relative">
                        {NAV.map((item) => (
                            <div
                                key={item.label}
                                className="relative"
                                onMouseEnter={() => {
                                    if (item.isMegaMenu || item.dropdown) {
                                        setDrop(item.label);
                                    } else {
                                        setDrop(null);
                                    }
                                }}
                            >
                                <div className="py-2">
                                    {item.isMegaMenu || item.dropdown ? (
                                        <span
                                            className={`px-4 py-2 text-base font-medium hover:text-aiblue transition-colors rounded-full flex items-center gap-1 cursor-pointer select-none ${
                                                drop === item.label
                                                    ? "text-aiblue"
                                                    : "text-gray-700"
                                            }`}
                                        >
                                            {item.label}
                                            <ChevronDown
                                                className={`h-3 w-3 transition-transform duration-200 ${drop === item.label ? "rotate-180" : ""}`}
                                            />
                                        </span>
                                    ) : (
                                        <Link
                                            to={item.to}
                                            className="px-4 py-2 text-base font-medium text-gray-700 hover:text-aiblue transition-colors rounded-full flex items-center gap-1"
                                        >
                                            {item.label}
                                        </Link>
                                    )}
                                </div>

                                {/* Standard Dropdown Panel */}
                                <AnimatePresence>
                                    {item.dropdown &&
                                        !item.isMegaMenu &&
                                        drop === item.label && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 6 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: 6 }}
                                                transition={{ duration: 0.15 }}
                                                className="absolute top-full left-0 mt-0 w-72 bg-white shadow-lg border border-gray-100 rounded-2xl p-2 z-50"
                                            >
                                                {item.dropdown.map((d) => (
                                                    <Link
                                                        key={d.label}
                                                        to={d.to}
                                                        onClick={() =>
                                                            setDrop(null)
                                                        }
                                                        className="block px-4 py-3 text-sm hover:bg-slate-50 rounded-xl transition-colors"
                                                    >
                                                        {d.label}
                                                    </Link>
                                                ))}
                                            </motion.div>
                                        )}
                                </AnimatePresence>
                            </div>
                        ))}
                    </div>

                    {/* CTA — Join Waitlist */}
                    <div
                        className="hidden lg:block relative"
                        onMouseEnter={() => setDrop(null)}
                    >
                        <Button
                            onClick={() => setCta(!cta)}
                            className="bg-aiblue hover:bg-blue-700 text-white rounded-full px-6 py-5 font-semibold cta-pulse"
                        >
                            {NAV_CTA_LABEL}
                            <ChevronDown
                                className={`ml-1 h-4 w-4 transition-transform duration-200 ${cta ? "rotate-180" : ""}`}
                            />
                        </Button>
                        <AnimatePresence>
                            {cta && (
                                <motion.div
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0 }}
                                    className="absolute right-0 top-full mt-2 w-72 bg-white shadow-lg border border-gray-100 rounded-2xl p-2 z-50"
                                >
                                    {NAV_CTA_ROLES.map((item) => (
                                        <button
                                            key={item.role}
                                            onClick={() =>
                                                goWaitlist(item.role)
                                            }
                                            className="w-full text-left px-4 py-3 hover:bg-slate-50 rounded-xl text-sm"
                                        >
                                            {item.label}
                                        </button>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Mobile hamburger */}
                    <button
                        className="lg:hidden p-2"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? (
                            <X className="h-6 w-6" />
                        ) : (
                            <Menu className="h-6 w-6" />
                        )}
                    </button>
                </motion.nav>
            </div>

            {/* Mobile menu */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="lg:hidden max-w-7xl mx-auto mt-2 bg-white/90 backdrop-blur-xl shadow-lg border border-gray-200 rounded-3xl p-4"
                    >
                        {NAV.map((it) =>
                            it.dropdown ? (
                                <div key={it.label}>
                                    <span className="block py-3 border-b border-gray-100 text-sm font-semibold text-gray-500 uppercase tracking-wide">
                                        {it.label}
                                    </span>
                                    {it.dropdown.map((d) => (
                                        <Link
                                            key={d.label}
                                            to={d.to}
                                            onClick={() => setOpen(false)}
                                            className="block py-3 pl-4 border-b border-gray-100 text-sm text-gray-700"
                                        >
                                            {d.label}
                                        </Link>
                                    ))}
                                </div>
                            ) : (
                                <Link
                                    key={it.label}
                                    to={it.to}
                                    onClick={() => setOpen(false)}
                                    className="block py-3 border-b last:border-0 border-gray-100"
                                >
                                    {it.label}
                                </Link>
                            ),
                        )}
                        <Button
                            onClick={() => {
                                setOpen(false);
                                navigate("/waitlist?role=student");
                            }}
                            className="w-full mt-3 bg-aiblue text-white rounded-full"
                        >
                            {NAV_CTA_LABEL}
                        </Button>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
